import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Activity, Clock, BarChart3, TrendingUp, Trash2 } from 'lucide-react';
import api from '../api/axios';

const ActivityDashboard = ({ isOpen, onClose, approvedCount = 0, liveCount = 0, deadCount = 0, totalCount = 0 }) => {
  const [activeTab, setActiveTab] = useState('recent');
  const [sessions, setSessions] = useState([]);
  const [creditHistory, setCreditHistory] = useState([]);

  const fetchSessions = async () => {
    // Don't fetch from storage - sessions are passed as props
    // This keeps data temporary (lost on reload)
    setSessions([]);
  };

  const fetchCreditHistory = async () => {
    try {
      const response = await api.get('/user/credit-history?limit=20');
      if (response.data.success) {
        setCreditHistory(response.data.history);
      }
    } catch (error) {
      console.error('Failed to fetch credit history');
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchSessions();
      fetchCreditHistory();
    }
  }, [isOpen]);

  const hitRate = totalCount > 0 ? (((liveCount + approvedCount) / totalCount) * 100).toFixed(1) : '0.0';

  const recentSessions = sessions.length > 0 ? sessions.map(s => {
    const total = s.total_cards || 0;
    const hits = (s.approved_cards || 0) + (s.live_cards || 0);
    const percentage = total > 0 ? ((hits / total) * 100).toFixed(1) : '0.0';
    
    return {
      id: s.api_name || s.gateway,
      approved: s.approved_cards || 0,
      live: s.live_cards || 0,
      dead: s.dead_cards || 0,
      total: total,
      percentage: percentage,
      date: new Date(s.started_at).toLocaleDateString('en-US', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }),
      status: s.speed ? s.speed.charAt(0).toUpperCase() + s.speed.slice(1) : 'Normal',
      creditsUsed: s.credits_used || ((s.approved_cards || 0) * 3 + (s.live_cards || 0) * 1)
    };
  }) : [
    { id: 'Current', approved: approvedCount, live: liveCount, dead: deadCount, total: totalCount, percentage: hitRate, date: new Date().toLocaleDateString('en-US', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }), status: 'Normal', creditsUsed: approvedCount * 3 + liveCount * 1 },
  ];

  const tabs = [
    { id: 'recent', label: 'Recent Sessions', icon: Clock },
    { id: 'trend', label: 'Session Trend', icon: TrendingUp },
    { id: 'hitrate', label: 'Hit Rate %', icon: BarChart3 },
    { id: 'credits', label: 'Credits Used', icon: Activity },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="w-full max-w-md bg-gradient-to-br from-[#1a1f3a] via-[#0f1729] to-[#1a1f3a] rounded-2xl border border-purple-500/20 shadow-2xl max-h-[85vh] overflow-hidden flex flex-col"
              style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)' }}
            >
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(139,92,246,0.05)_0%,_transparent_100%)]"></div>

              <div className="relative">
                <div className="sticky top-0 bg-gradient-to-b from-[#1a1f3a] to-transparent p-6 pb-4 border-b border-purple-500/10 backdrop-blur-xl z-10">
                  <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-white/5 rounded-lg transition-colors">
                    <X size={20} className="text-gray-400" />
                  </button>

                  <motion.div
                    className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4 mx-auto"
                    animate={{
                      boxShadow: [
                        '0 0 20px rgba(168, 85, 247, 0.4)',
                        '0 0 40px rgba(168, 85, 247, 0.6)',
                        '0 0 20px rgba(168, 85, 247, 0.4)',
                      ],
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Activity size={24} className="text-white" />
                  </motion.div>

                  <h2 className="text-2xl font-bold text-white text-center mb-1">Activity Dashboard</h2>
                  <p className="text-gray-400 text-sm text-center px-2">Your complete checking history with analytics</p>
                </div>

                <div className="px-6 py-4 space-y-4 overflow-y-auto max-h-[calc(85vh-200px)]">
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {tabs.map((tab, index) => {
                      const Icon = tab.icon;
                      const isActive = activeTab === tab.id;

                      return (
                        <motion.button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap transition-all ${
                            isActive
                              ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-400/30 text-purple-300'
                              : 'bg-white/5 border border-white/5 text-gray-400 hover:bg-white/10'
                          }`}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <Icon size={14} />
                          <span className="text-sm font-medium">{tab.label}</span>
                        </motion.button>
                      );
                    })}
                  </div>

                  <AnimatePresence mode="wait">
                    {activeTab === 'recent' && (
                      <motion.div
                        key="recent"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="space-y-3"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <Clock size={18} className="text-cyan-400" />
                          <h3 className="text-lg font-bold text-white">Recent Sessions</h3>
                        </div>

                        {recentSessions.map((session, index) => (
                          <motion.div
                            key={session.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="p-4 rounded-xl bg-gradient-to-br from-white/5 to-white/5 backdrop-blur-xl border border-white/10 hover:border-cyan-400/30 transition-all"
                          >
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                                <span className="text-white font-semibold">{session.id}</span>
                              </div>
                              <div className="px-3 py-1 rounded-full bg-cyan-500/20 border border-cyan-400/30">
                                <span className="text-cyan-400 text-xs font-semibold">{session.status}</span>
                              </div>
                            </div>

                            <div className="flex items-center gap-4 mb-2">
                              <div className="flex items-center gap-1">
                                <div className="w-1.5 h-1.5 rounded-full bg-cyan-400"></div>
                                <span className="text-cyan-400 text-sm font-semibold">{session.approved}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <div className="w-1.5 h-1.5 rounded-full bg-green-400"></div>
                                <span className="text-green-400 text-sm font-semibold">{session.live}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <div className="w-1.5 h-1.5 rounded-full bg-red-400"></div>
                                <span className="text-red-400 text-sm font-semibold">{session.dead}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <div className="w-1.5 h-1.5 rounded-full bg-purple-400"></div>
                                <span className="text-purple-400 text-sm font-semibold">{session.percentage}%</span>
                              </div>
                              {session.creditsUsed && (
                                <div className="flex items-center gap-1">
                                  <div className="w-1.5 h-1.5 rounded-full bg-yellow-400"></div>
                                  <span className="text-yellow-400 text-sm font-semibold">-{session.creditsUsed}</span>
                                </div>
                              )}
                            </div>

                            <div className="text-gray-400 text-xs">{session.date}</div>
                          </motion.div>
                        ))}
                      </motion.div>
                    )}

                    {activeTab === 'trend' && (
                      <motion.div
                        key="trend"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="space-y-3"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <TrendingUp size={18} className="text-cyan-400" />
                          <h3 className="text-lg font-bold text-white">Session Trend</h3>
                        </div>

                        {recentSessions.length > 0 ? (
                          <div className="space-y-3">
                            {recentSessions.slice(0, 5).map((session, index) => (
                              <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="p-4 rounded-xl bg-gradient-to-br from-white/5 to-white/5 backdrop-blur-xl border border-white/10"
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-white font-semibold text-sm">{session.id}</span>
                                  <span className="text-gray-400 text-xs">{session.date}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-gray-400 text-xs">Hit Rate</span>
                                      <span className="text-purple-400 text-sm font-bold">{session.percentage}%</span>
                                    </div>
                                    <div className="h-2 bg-gray-700/50 rounded-full overflow-hidden">
                                      <motion.div
                                        className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                                        initial={{ width: 0 }}
                                        animate={{ width: `${session.percentage}%` }}
                                        transition={{ duration: 1, delay: index * 0.1 }}
                                      />
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xs text-gray-400">Total</p>
                                    <p className="text-white font-bold">{session.approved + session.live + session.dead}</p>
                                  </div>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <TrendingUp size={48} className="text-gray-600 opacity-30 mx-auto mb-3" />
                            <p className="text-gray-400 text-sm">No session data yet</p>
                          </div>
                        )}
                      </motion.div>
                    )}

                    {activeTab === 'hitrate' && (
                      <motion.div
                        key="hitrate"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="space-y-3"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <BarChart3 size={18} className="text-green-400" />
                          <h3 className="text-lg font-bold text-white">Hit Rate %</h3>
                        </div>

                        {recentSessions.length > 0 ? (
                          <>
                            {/* Overall Hit Rate */}
                            <div className="p-6 rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20">
                              <div className="text-center">
                                <p className="text-gray-400 text-sm mb-2">Overall Hit Rate</p>
                                <motion.p
                                  className="text-5xl font-bold text-green-400"
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{ type: "spring", stiffness: 200 }}
                                >
                                  {hitRate}%
                                </motion.p>
                                <p className="text-gray-500 text-xs mt-2">
                                  {liveCount + approvedCount} hits / {totalCount} total
                                </p>
                              </div>
                            </div>

                            {/* Per Session Hit Rates */}
                            <div className="space-y-2">
                              {recentSessions.slice(0, 5).map((session, index) => (
                                <div key={index} className="p-3 rounded-lg bg-white/5 border border-white/10">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-white text-sm font-semibold">{session.id}</span>
                                    <span className="text-green-400 font-bold">{session.percentage}%</span>
                                  </div>
                                  <div className="h-2 bg-gray-700/50 rounded-full overflow-hidden">
                                    <motion.div
                                      className="h-full bg-gradient-to-r from-green-500 to-emerald-400"
                                      initial={{ width: 0 }}
                                      animate={{ width: `${session.percentage}%` }}
                                      transition={{ duration: 1, delay: index * 0.1 }}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <div className="text-center py-8">
                            <BarChart3 size={48} className="text-gray-600 opacity-30 mx-auto mb-3" />
                            <p className="text-gray-400 text-sm">No hit rate data yet</p>
                          </div>
                        )}
                      </motion.div>
                    )}

                    {activeTab === 'credits' && (
                      <motion.div
                        key="credits"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="space-y-3"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <Activity size={18} className="text-cyan-400" />
                          <h3 className="text-lg font-bold text-white">Credits Used</h3>
                        </div>

                        <div className="p-6 rounded-xl bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-500/20">
                          <div className="text-center mb-4">
                            <p className="text-gray-400 text-sm mb-2">Total Credits Spent (All Time)</p>
                            <motion.p
                              className="text-5xl font-bold text-yellow-400"
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ type: "spring", stiffness: 200 }}
                            >
                              {creditHistory.reduce((total, item) => total + (item.credits_deducted || 0), 0)}
                            </motion.p>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-orange-500/10 rounded-lg p-3 border border-orange-500/20">
                              <p className="text-orange-400 text-xs mb-1">Approved Checks</p>
                              <p className="text-white font-bold">{creditHistory.filter(h => h.result === 'APPROVED').length}</p>
                            </div>
                            <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/20">
                              <p className="text-green-400 text-xs mb-1">Live Checks</p>
                              <p className="text-white font-bold">{creditHistory.filter(h => h.result === 'LIVE').length}</p>
                            </div>
                          </div>
                        </div>

                        {/* Detailed Credit History from Database */}
                        {creditHistory.length > 0 && (
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            <h4 className="text-white font-semibold text-sm mb-2">Detailed Credit History</h4>
                            {creditHistory.map((item, index) => (
                              <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className={`p-3 rounded-lg border ${
                                  item.result === 'APPROVED' ? 'bg-orange-900/20 border-orange-500/30' :
                                  item.result === 'LIVE' ? 'bg-green-900/20 border-green-500/30' :
                                  'bg-gray-900/20 border-gray-500/30'
                                }`}
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex-1">
                                    <p className="text-white text-xs font-mono">{item.card_number}</p>
                                    <p className="text-gray-400 text-xs">{item.api_name || item.gateway}</p>
                                  </div>
                                  <div className="text-right">
                                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                      item.result === 'APPROVED' ? 'bg-orange-500/20 text-orange-400' :
                                      item.result === 'LIVE' ? 'bg-green-500/20 text-green-400' :
                                      item.result === 'ERROR' ? 'bg-red-500/20 text-red-400' :
                                      'bg-gray-500/20 text-gray-400'
                                    }`}>
                                      {item.result}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-500 text-xs">
                                    {new Date(item.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                  <span className={`text-sm font-bold ${
                                    item.credits_deducted > 0 ? 'text-red-400' : 'text-gray-500'
                                  }`}>
                                    {item.credits_deducted > 0 ? `-${item.credits_deducted}` : '0'}
                                  </span>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ActivityDashboard;
